import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Divider, TextField, Typography } from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { shiftLabel } from '../utils/attendance';

const errorMessage = error => {
    const details = error.response?.data;
    return details?.detail || (details && Object.values(details).flat().join(' ')) || 'Não foi possível guardar. Tente novamente.';
};

const OvertimeEditor = ({ record, index, apiBaseURL, onChange }) => {
    const [hours, setHours] = useState(String(record.horas_trabalhadas));
    const [reason, setReason] = useState(record.justificativa || '');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const valid = /^\d+(\.\d{1,2})?$/.test(hours) && Number(hours) > 0 && Number(hours) <= 999.99;
    const changed = Number(hours) !== Number(record.horas_trabalhadas) || reason !== (record.justificativa || '');

    const save = async (remove = false) => {
        setBusy(true);
        setError('');
        const config = { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } };
        try {
            if (remove) {
                await axios.delete(`${apiBaseURL}/api/horas-extras/${record.id}/`, config);
                onChange(record.id, null);
            } else {
                const response = await axios.patch(`${apiBaseURL}/api/horas-extras/${record.id}/`, {
                    horas_trabalhadas: hours, justificativa: reason,
                }, config);
                onChange(record.id, { ...record, ...response.data });
            }
        } catch (requestError) {
            setError(errorMessage(requestError));
        } finally {
            setBusy(false);
        }
    };

    return <Box component="form" onSubmit={event => { event.preventDefault(); if (valid && changed && !busy) save(); }}
        aria-label={`Registo de horas ${index + 1}`} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Registo {index + 1}</Typography>
        <TextField fullWidth size="small" type="number" label={`Horas do registo ${index + 1}`} value={hours}
            onChange={event => setHours(event.target.value)} disabled={busy}
            inputProps={{ min: 0.01, max: 999.99, step: 0.01 }} helperText="Horas decimais; 1,5 h = 1 h e 30 min." />
        <TextField fullWidth size="small" label={`Justificação do registo ${index + 1}`} value={reason}
            onChange={event => setReason(event.target.value)} disabled={busy} multiline minRows={2} sx={{ mt: 1.5 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {record.aprovado_por ? `Aprovado por ${record.aprovado_por}${record.data_aprovacao ? ` em ${moment(record.data_aprovacao).format('DD/MM/YYYY')}` : ''}` : 'Sem aprovação registada'}
        </Typography>
        {error && <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>}
        {confirmDelete ? <Box sx={{ mt: 1.5 }}>
            <Typography variant="body2">Remover este registo de {record.horas_trabalhadas} h?</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button color="error" disabled={busy} onClick={() => save(true)}>Confirmar remoção</Button>
                <Button disabled={busy} onClick={() => setConfirmDelete(false)}>Cancelar</Button>
            </Box>
        </Box> : <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 1.5 }}>
            <Button color="error" disabled={busy} onClick={() => setConfirmDelete(true)}>Remover registo</Button>
            <Button type="submit" variant="contained" disabled={!valid || !changed || busy}>{busy ? 'A guardar…' : 'Guardar horas'}</Button>
        </Box>}
    </Box>;
};

const AttendanceDayDetails = ({ date, details, apiBaseURL, onOvertimeChange, onAddOvertime }) => {
    if (!date || !details) return <Box sx={{ p: 3 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Detalhes do dia</Typography>
        <Typography color="text.secondary">Carregue num dia do calendário para consultar as presenças e editar as horas.</Typography>
    </Box>;

    const hasAbsences = Boolean(details.absences.length || details.vacations.length || details.sickLeave.length);
    return <Box component="section" aria-label="Detalhes do dia" sx={{ p: { xs: 2, sm: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
            <Typography variant="overline" color="text.secondary">Dia selecionado</Typography>
            <Typography component="h2" variant="h6" sx={{ fontWeight: 700 }}>{moment(date).format('dddd, DD [de] MMMM')}</Typography>
            <Typography variant="body2" color="text.secondary">Turno: {shiftLabel(details.shift)}{details.shiftChanged ? ' · alterado neste dia' : ' · habitual'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {details.holidays.map((item, index) => <Chip key={index} size="small" label={`Feriado: ${item.name}`} sx={{ bgcolor: '#fef08a' }} />)}
            {details.vacations.map((item, index) => <Chip key={index} size="small" label={item.half_day ? 'Meio dia de férias' : 'Férias'} sx={{ bgcolor: '#fecaca' }} />)}
            {details.sickLeave.length > 0 && <Chip size="small" label="Baixa médica" sx={{ bgcolor: '#ddd6fe' }} />}
            {details.absences.length > 0 && <Chip size="small" label="Falta" sx={{ bgcolor: '#fed7aa' }} />}
            {!hasAbsences && !details.holidays.length && <Chip size="small" label={moment(date).isoWeekday() > 5 ? 'Fim de semana' : 'Dia útil'} />}
        </Box>
        {details.absences.map((item, index) => <Alert key={item.id || index} severity="warning">
            <Typography variant="body2">Falta {item.tipo_falta}: {item.horario_inicio?.slice(0, 5)}–{item.horario_fim?.slice(0, 5)}</Typography>
            {item.documento_justificacao && <Button size="small" component="a" href={new URL(item.documento_justificacao, apiBaseURL).href} target="_blank" rel="noopener noreferrer">Ver justificação</Button>}
        </Alert>)}
        {details.sickLeave.map((item, index) => <Alert key={item.id || index} severity="info">
            Baixa de {moment(item.data_inicio).format('DD/MM/YYYY')} a {moment(item.data_fim).format('DD/MM/YYYY')}{item.descricao ? ` · ${item.descricao}` : ''}
            {item.documento_medico && <Button size="small" component="a" href={new URL(item.documento_medico, apiBaseURL).href} target="_blank" rel="noopener noreferrer">Ver documento médico</Button>}
        </Alert>)}
        <Divider />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Horas extra do dia</Typography>
            <Chip color="primary" label={`${details.total.toLocaleString('pt-PT')} h`} />
        </Box>
        {!details.overtime.length && <Typography variant="body2" color="text.secondary">Sem horas extra registadas neste dia.</Typography>}
        {details.overtime.map((record, index) => <OvertimeEditor key={`${record.id}:${record.horas_trabalhadas}:${record.justificativa || ''}`}
            record={record} index={index} apiBaseURL={apiBaseURL} onChange={onOvertimeChange} />)}
        <Button variant="outlined" onClick={onAddOvertime}>Adicionar horas neste dia</Button>
    </Box>;
};

export default AttendanceDayDetails;
